import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api, setStoredToken, formatApiError } from "../lib/api";
import useStore from "../store/useStore";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const loadServerState = useStore((s) => s.loadServerState);
  const clearServerState = useStore((s) => s.clearServerState);

  // on mount, check existing session
  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const { data } = await api.get("/auth/me");
        if (mounted) {
          setUser(data);
          await loadServerState();
        }
      } catch {
        // boot 401 → wipe any stale Bearer token from a previous deploy
        // (otherwise the interceptor keeps re-sending it on every request,
        // which on mobile could surface as a render that goes blank after
        // an unhandled axios reject inside a component effect).
        if (mounted) {
          setStoredToken(null);
          setUser(null);
        }
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => {
      mounted = false;
    };
  }, [loadServerState]);

  const signup = useCallback(
    async (payload) => {
      const { data } = await api.post("/auth/signup", payload);
      if (data.accessToken) setStoredToken(data.accessToken);
      setUser(data);
      await loadServerState();
      return data;
    },
    [loadServerState]
  );

  const login = useCallback(
    async (email, password) => {
      const { data } = await api.post("/auth/login", { email, password });
      if (data.accessToken) setStoredToken(data.accessToken);
      setUser(data);
      await loadServerState();
      return data;
    },
    [loadServerState]
  );

  const logout = useCallback(async () => {
    try {
      await api.post("/auth/logout");
    } catch {
      /* ignore */
    }
    setStoredToken(null);
    setUser(null);
    clearServerState();
    // wipe the persisted zustand cache so a different user logging in on
    // the same browser doesn't briefly see the previous user's customShows
    // or stale saves.
    try {
      localStorage.removeItem("the-lobby-store");
    } catch {
      /* ignore */
    }
  }, [clearServerState]);

  const value = { user, loading, signup, login, logout, formatApiError };
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
